import { QueryResult, useQuery } from '@apollo/client'
import * as React from 'react'

import Button from '../../../../common/src/components/Button'
import Icon from '../../../../common/src/components/Icon'
import { InlineErrorBoundary } from '../../../../common/src/components/InlineErrorBoundary'
import { TasksTableMultiselect } from '../../../../common/src/components/TasksTableMultiselect'
import {
  AlignRight,
  BodyMonoText,
  BodyText,
  BodyTextSpan,
  H1,
  H3,
  H5,
  InputLarge,
  SmallerText,
  Spacer,
} from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { fetchFirmTasksQuery } from '../../../../common/src/data/queries'
import { dateFmt } from '../../../../common/src/date'
import {
  BulkUpdateOp,
  FetchFirmTasksQuery,
  FetchFirmTasksQueryVariables,
  UpdateTasksMutation,
} from '../../../../common/src/types.gen'
import validate from '../../../../common/src/validate'
import { bulkUpdateTasks } from '../../graphql/mutations'
import TextAreaAutosize from '../components/TextAreaAutosize'
import { getTasksNotUpdated, renderFieldError, renderTasksList } from './BulkUpdatePage'

interface LeasingSpecialSectionProps {
  firmId?: number
  onUpdate: () => void
}

interface LeasingSpecialsImplProps {
  onUpdate: () => void
}

type LeasingTask = UpdateTasksMutation['bulkUpdateTasks'][0]

interface LeasingSpecialsSectionState {
  specialText?: string
  endDate?: string
  selectedTaskIds: Set<string | number>
  errorSpecial: string
  errorEndDate: string
  errorTaskIds: string
  submitting: boolean
  saveSuccess: boolean
  updatedTasks: LeasingTask[]
}

class LeasingSpecialSectionImpl extends React.PureComponent<
  LeasingSpecialsImplProps &
    FetchFirmTasksQueryVariables &
    QueryResult<FetchFirmTasksQuery, FetchFirmTasksQueryVariables>,
  LeasingSpecialsSectionState
> {
  state = {
    specialText: '',
    endDate: '',
    selectedTaskIds: new Set<string | number>(),
    errorSpecial: '',
    errorEndDate: '',
    errorTaskIds: '',
    submitting: false,
    saveSuccess: false,
    updatedTasks: [] as LeasingTask[],
  }

  /**
   * The end date text input element. Optional: tells us when the special expires.
   */
  private endDateRef = React.createRef<HTMLInputElement>()

  private onSelectedTaskIdsChange = (selectedTaskIds: Set<string | number>) => this.setState({ selectedTaskIds })

  /**
   * Prove any end date is (a) in ISO format (b) in the future
   */
  private validateEndDate = (endDate: string) => {
    if (endDate.length > 0 && validate.isoDate().validate(endDate).error) {
      return 'should look like: YYYY-MM-DD'
    } else if (new Date() >= new Date(endDate)) {
      return 'please pick a future date'
    }
    return ''
  }

  private onSaveUpdate = () => {
    const { onUpdate } = this.props
    const { specialText, selectedTaskIds } = this.state

    this.setState({ submitting: true })

    const leasingSpecialText = specialText.trim()

    let errorSpecial = ''
    let errorEndDate = ''
    let errorTaskIds = ''

    if (leasingSpecialText.length === 0) {
      errorSpecial = 'please enter the special text'
    }

    const endDate = this.endDateRef.current!.value.trim()
    errorEndDate = this.validateEndDate(endDate)
    if (errorEndDate !== '') {
      this.setState({ endDate })
    }

    if (selectedTaskIds.size === 0) {
      errorTaskIds = 'please pick a property'
    }

    this.setState({ errorSpecial, errorEndDate, errorTaskIds })

    if (errorSpecial || errorEndDate || errorTaskIds) {
      this.setState({ submitting: false })
      return
    }

    const taskIds = Array.from(selectedTaskIds).map(id => parseInt(id.toString(), 10))

    const leasingSpecialStr = JSON.stringify({
      specialText,
      endDate,
    })

    bulkUpdateTasks({
      taskIds,
      field: BulkUpdateOp.LEASING_SPECIAL,
      value: leasingSpecialStr,
    })
      .then(res => {
        onUpdate()
        this.setState({
          saveSuccess: true,
          submitting: false,
          updatedTasks: res.data!.bulkUpdateTasks,
          endDate,
        })
      })
      .catch(handleError)
  }

  /**
   * Every time the user changes the value of the TextArea, save special text to page state
   */
  private setSpecialsText = (specialText: string) => this.setState({ specialText })

  /**
   *
   */
  private leasingSpecialForm = () => {
    const { data, firmId } = this.props
    const { submitting, specialText, errorSpecial, errorEndDate, errorTaskIds } = this.state

    if (firmId == null) {
      return null
    }

    if (data == null || data.firm == null || data.firm.tasks == null) {
      return null
    }

    return (
      <>
        <H5>What’s the text of the special?</H5>
        <Spacer height={16} />
        <TextAreaAutosize
          value={specialText}
          hasError={errorSpecial !== ''}
          placeholder={'$100 off for new residents who…'}
          onChange={this.setSpecialsText}
        />
        {errorSpecial && renderFieldError(errorSpecial)}
        <Spacer height={16} />
        <H5>… and when does it expire?</H5>
        <Spacer height={16} />
        <InputLarge hasError={errorEndDate !== ''} placeholder="2020-05-01" ref={this.endDateRef} type="date" />
        {errorEndDate && renderFieldError(errorEndDate)}
        <Spacer height={48} />
        <BodyText color="silver">
          <Icon name="thread-editing" color="lemon" />
          <Spacer width={8} />
          Replaces old specials
        </BodyText>
        <Spacer height={8} />
        <SmallerText>Your update will replace the old leasing special at all properties selected below.</SmallerText>
        <Spacer height={64} />
        <H3>For which properties?</H3>
        <Spacer height={32} />
        <TasksTableMultiselect tasks={data.firm.tasks} onChange={this.onSelectedTaskIdsChange} />
        {errorTaskIds && renderFieldError(errorTaskIds)}
        <Spacer height={32} />
        <AlignRight>
          <Button disabled={submitting} onClick={this.onSaveUpdate}>
            Save Update
          </Button>
        </AlignRight>
      </>
    )
  }

  private leasingSpecialSuccess = () => {
    const { data } = this.props
    const { specialText, endDate, selectedTaskIds, updatedTasks } = this.state

    if (data == null) {
      return null
    }

    const tasksNotUpdated = getTasksNotUpdated(data.firm.tasks, selectedTaskIds, updatedTasks)

    return (
      <>
        <H1>Thank you!</H1>
        {updatedTasks.length > 0 && (
          <>
            <Spacer height={64} />
            <H3 color="silver">You added this special … </H3>
            <Spacer height={16} />
            <blockquote>
              <BodyMonoText>
                <BodyTextSpan>“</BodyTextSpan>
                {specialText.trim()}
                <BodyTextSpan>”</BodyTextSpan>
              </BodyMonoText>
            </blockquote>
            {endDate.length > 0 && (
              <blockquote>
                <Spacer height={16} />
                <H5 color="silver">… expiring on …</H5>
                <Spacer height={16} />
                <BodyMonoText>{dateFmt(endDate, 'UTC')}</BodyMonoText>
              </blockquote>
            )}
            <Spacer height={48} />
            <H3 color="silver">… to these tasks … </H3>
            <Spacer height={24} />
            {renderTasksList(updatedTasks)}
          </>
        )}
        {tasksNotUpdated.length > 0 && (
          <>
            <Spacer height={16} />
            <H3 color="silver">{specialText} already exists on the following properties and was not added:</H3>
            <Spacer height={16} />
            {renderTasksList(tasksNotUpdated)}
          </>
        )}
      </>
    )
  }

  render() {
    const { saveSuccess } = this.state

    return saveSuccess ? this.leasingSpecialSuccess() : this.leasingSpecialForm()
  }
}

export default function LeasingSpecialSection(props: LeasingSpecialSectionProps) {
  const { firmId } = props

  if (firmId == null) {
    return null
  }

  const res = useQuery<FetchFirmTasksQuery, FetchFirmTasksQueryVariables>(fetchFirmTasksQuery, {
    variables: { firmId },
  })

  if (res.loading || res.error) {
    return null
  }

  return (
    <InlineErrorBoundary componentName="LeasingSpecialSection">
      <LeasingSpecialSectionImpl firmId={firmId} {...props} {...res} />
    </InlineErrorBoundary>
  )
}
